import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { initParticlesEngine } from "@tsparticles/react";
import { tsParticles } from "@tsparticles/engine";
import { loadSlim } from "@tsparticles/slim";
import { loadTextShape } from "@tsparticles/shape-text";
import { particleSkills } from "./particleSkills";

const Particles = ({ isHomeActive }) => {
  const mountedRef = useRef(false);
  const containerRef = useRef(null);
  const [containerReady, setContainerReady] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [particlesCount, setParticlesCount] = useState(45);
  const remainingSkillsRef = useRef([]);
  const skills = useMemo(() => {
    const maxSkills = Math.max(1, Math.floor(particlesCount / 2));
    return particleSkills.slice(0, Math.min(particleSkills.length, maxSkills));
  }, [particlesCount]);

  const resetRemainingSkills = useCallback(() => {
    remainingSkillsRef.current = [...skills];
  }, [skills]);
  useEffect(() => {
    const count = Math.max(20, Math.round(window.innerWidth * window.innerHeight / 48000));
    setParticlesCount(count);
    const uaMobile =
      typeof navigator !== "undefined" &&
      navigator.userAgentData &&
      typeof navigator.userAgentData.mobile === "boolean"
        ? navigator.userAgentData.mobile
        : false;
    setIsMobile(uaMobile);
  }, []);

  useEffect(() => {
    resetRemainingSkills();
  }, [resetRemainingSkills]);

  const convertToSkillParticle = useCallback(
    (particle) => {
      if (!particle) return;
      if (!skills.length) return;
      let skill;
      if (remainingSkillsRef.current.length > 0) {
        const index = Math.floor(Math.random() * remainingSkillsRef.current.length);
        skill = remainingSkillsRef.current.splice(index, 1)[0];
      } else {
        skill = skills[Math.floor(Math.random() * skills.length)];
      }
      const withValue = (current, value) =>
        current && typeof current.value !== "undefined"
          ? { ...current, value }
          : { value };
      particle.shape = "text";
      particle.color = {
        h: { value: 0, enable: false },
        s: { value: 85, enable: false },
        l: { value: 100, enable: false },
      };
      particle.opacity = withValue(particle.opacity, 1);
      particle.size = withValue(particle.size, isMobile ? 20 : 10);
      particle.shapeData = {
        value: skill,
        font: "Verdana",
        style: "normal",
        weight: "600",
      };
      particle.text = skill;
    },
    [isMobile, skills]
  );

  const convertToDotParticle = useCallback(
    (particle) => {
      if (!particle) return;
      particle.shape = "circle";
      particle.shapeData = undefined;
      particle.text = undefined;
      particle.color = {
        h: { value: 0, enable: false },
        s: { value: 0, enable: false },
        l: { value: 100, enable: false },
      };
      particle.opacity = { value: 0.35 };
      particle.size = { value: isMobile ? 5 : 6 };
    },
    [isMobile]
  );

  const options = useMemo(
    () => ({
      fullScreen: {
        enable: false
      },
      particles: {
        number: {
          value: particlesCount,
          density: {
            enable: false
          },
        },
        shape: {
          type: "circle",
        },
        size: {
          value: isMobile ? 5 : 6,
          random: false,
        },
        opacity: {
          value: 0.35,
          animation: {
            enable: false
          },
        },
        links: {
          enable: true,
          distance: isMobile ? 200 : 250,
          color: "#ffffff",
          opacity: 0.4,
          width: 2
        },
        move: {
          enable: true,
          speed: isMobile ? 1.1 : 1.33,
          direction: "none",
          random: false,
          straight: false,
          outModes: {
            default: "bounce"
          },
          attract: {
            enable: true,
            distance: 150
          },
          collisions: false
        },

      },
      interactivity: {
        events: {
          onHover: {
            enable: !isMobile,
            mode: "grab"
          },
          onClick: {
            enable: false,
            mode: "bubble"
          },
          resize: false
        },
        modes: {
          grab: {
            distance: 200,
            links: {
              opacity: 1
            }
          },
          bubble: {
            distance: 200,
            size: 40,
            duration: 2,
            opacity: 0.8,
            speed: 3
          },
          repulse: {
            distance: 200
          },
          push: {
            quantity: 4
          },
          remove: {
            quantity: 2
          }
        }
      },
    }),
    [isMobile, particlesCount]
  );

  useEffect(() => {
    let active = true;
    initParticlesEngine(async (engine) => {
      await loadSlim(engine);
      await loadTextShape(engine);
    }).then(() => {
      if (!active) return;
      if (!mountedRef.current) {
        const existing = tsParticles
          .dom()
          .find((container) => container.id === "main-particles");
        if (!existing) {
          tsParticles.load({
            id: "main-particles",
            options,
          }).then((container) => {
            containerRef.current = container ?? null;
            setContainerReady(!!container);
          });
        } else {
          containerRef.current = existing;
          setContainerReady(true);
        }
        mountedRef.current = true;
      }
    });
    return () => {
      active = false;
    };
  }, [options]);

  useEffect(() => {
    if (mountedRef.current && containerRef.current) {
      containerRef.current.reset(options);
    }
  }, [options]);

  useEffect(() => {
    const handleClick = (event) => {
      if (!isHomeActive) return;
      const container =
        containerRef.current ??
        tsParticles.dom().find((item) => item.id === "main-particles");
      if (!container || !container.canvas?.element) {
        return;
      }
      const rect = container.canvas.element.getBoundingClientRect();
      const pos = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };
      const pixelRatio = container.retina.pixelRatio;
      const particles = container.particles.quadTree.queryCircle(
        { x: pos.x * pixelRatio, y: pos.y * pixelRatio },
        40 * pixelRatio
      );
      if (!particles.length) {
        return;
      }
      const target = particles[0];
      convertToSkillParticle(target);
    };
    window.addEventListener("click", handleClick);
    return () => {
      window.removeEventListener("click", handleClick);
    };
  }, [convertToSkillParticle, isHomeActive]);

  useEffect(() => {
    let intervalId;
    let timeoutId;
    const container =
      containerRef.current ??
      tsParticles.dom().find((item) => item.id === "main-particles");
    if (!container) return;

    if (!isHomeActive) {
      intervalId = setInterval(() => {
        const texts = container.particles.filter(
          (particle) => particle.shape === "text"
        );
        if (!texts.length) {
          clearInterval(intervalId);
          return;
        }
        const target = texts[Math.floor(Math.random() * texts.length)];
        convertToDotParticle(target);
      }, 200);
      return () => clearInterval(intervalId);
    }

    resetRemainingSkills();
    const resetTexts = container.particles.filter(
      (particle) => particle.shape === "text"
    );
    resetTexts.forEach(convertToDotParticle);

    timeoutId = setTimeout(() => {
      intervalId = setInterval(() => {
        const dots = container.particles.filter(
          (particle) => particle.shape !== "text"
        );
        if (!dots.length) return;
        const target = dots[Math.floor(Math.random() * dots.length)];
        convertToSkillParticle(target);
      }, 2000);
    }, 5000);

    return () => {
      clearTimeout(timeoutId);
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [
    convertToSkillParticle,
    convertToDotParticle,
    isHomeActive,
    containerReady,
    resetRemainingSkills,
  ]);

  return (
    <div
      id="main-particles"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1,
        opacity: 0.3,
        pointerEvents: "none",
      }}
    />
  );
};

export default Particles;
